// UtilityMs990ExtensionPage.tsx — /utility/ms990ext
// MS 990 Batch Extension utility:
//   Pull 990 returns awaiting extension transmission → run a test on one →
//   process the rest. Per-return pipeline:
//     pre-flight → import 8868 worksheet → calc → print → save 8868 to M-Files →
//     (if not already accepted by IRS) qualify → upload-and-hold → release
//
// Returns are processed one at a time; user must NOT have any of them open in CCH.

import React, { useCallback, useMemo, useState } from 'react';
import {
  Play, Loader2, CheckCircle2, XCircle, AlertTriangle, FlaskConical, Database,
  ChevronRight, ChevronDown, Download, FileSpreadsheet, ExternalLink, RotateCcw,
} from 'lucide-react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
  pullCandidates, fetchCompletedAudit, processOne,
  type MS990ExtCandidate, type ProcessResult, type ProcessStep,
} from '../../services/utilMs990Ext';

interface RowState {
  candidate: MS990ExtCandidate;
  state: 'pending' | 'running' | 'done' | 'skipped' | 'failed';
  result?: ProcessResult;
}

// Concurrency for "Execute All". Each row is a unique ReturnID so workers don't
// collide. CCH rate-limits are the real constraint — 4 streams × ~25 sequential
// calls per return over ~3 min stays well under CCH's req/sec limit.
// cchClient.cchFetch already has 429-retry logic as a safety net.
// Don't push higher than 4-5 without testing — upload-and-hold is the bursty step.
const EXECUTE_CONCURRENCY = 4;

// Steps in display order — labels for the per-step progress dots
const STEP_DEFS: { key: string; label: string }[] = [
  { key: 'preflight',       label: 'Pre-flight' },
  { key: 'clientGuid',      label: 'Client GUID' },
  { key: 'checkAccepted',   label: 'Already filed?' },
  { key: 'readReturnType',  label: 'Read type' },
  { key: 'importExtension', label: 'Import 8868' },
  { key: 'calculate',       label: 'Calculate' },
  { key: 'print',           label: 'Print' },
  { key: 'extract8868',     label: 'Extract 8868' },
  { key: 'saveMFiles',      label: 'Save to M-Files' },
  { key: 'qualify',         label: 'Qualify' },
  { key: 'uploadAndHold',   label: 'Upload' },
  { key: 'release',         label: 'Release' },
];

function StepDot({ status }: { status?: ProcessStep['status'] }) {
  const color =
    status === 'ok'      ? 'var(--good)' :
    status === 'skipped' ? 'var(--muted)' :
    status === 'error'   ? 'var(--bad)' : 'rgba(255,255,255,.12)';
  const ch = status === 'ok' ? '✓' : status === 'skipped' ? '–' : status === 'error' ? '✕' : '·';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 18, height: 18, borderRadius: '50%',
      background: status ? `${color}22` : 'transparent',
      border: `1px solid ${color}`, color, fontSize: 11, fontWeight: 700,
    }}>{ch}</span>
  );
}

function StepProgress({ steps }: { steps?: ProcessStep[] }) {
  const map = new Map((steps || []).map((s) => [s.name, s]));
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {STEP_DEFS.map((def) => {
        const s = map.get(def.key);
        return (
          <span key={def.key} title={`${def.label}${s?.message ? ` — ${s.message}` : ''}`}>
            <StepDot status={s?.status} />
          </span>
        );
      })}
    </div>
  );
}

export default function UtilityMs990ExtensionPage() {
  const [rows, setRows] = useState<RowState[]>([]);
  const [pulling, setPulling] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirmExec, setConfirmExec] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [testedIdx, setTestedIdx] = useState<number | null>(null);
  const [hiddenCompletedCount, setHiddenCompletedCount] = useState(0);

  // ── Pull ────────────────────────────────────────────────────────────────────
  // Pulls the SQL candidate list AND the completion audit log, then filters out
  // returnIds that were successfully processed in a prior run (audit log is the
  // only persistent server-side state — no per-pull CCH probing, so no rate
  // limit risk). Failed/skipped rows are NOT in the audit and will reappear,
  // matching the "only show ones that didn't complete" expectation.
  const handlePull = useCallback(async () => {
    setPulling(true);
    try {
      const [candidates, completed] = await Promise.all([
        pullCandidates(),
        fetchCompletedAudit(),
      ]);
      const remaining = candidates.filter((c) => !completed.has(c.ReturnID));
      const hidden = candidates.length - remaining.length;
      setRows(remaining.map((c) => ({ candidate: c, state: 'pending' })));
      setExpandedIdx(null);
      setTestedIdx(null);
      setHiddenCompletedCount(hidden);
      toast.info(
        hidden > 0
          ? `Pulled ${candidates.length} candidate${candidates.length === 1 ? '' : 's'} — ${hidden} previously completed (hidden), ${remaining.length} to process`
          : `Pulled ${candidates.length} candidate${candidates.length === 1 ? '' : 's'}`
      );
    } catch (err: any) {
      toast.error(`Pull failed: ${err.message}`);
    } finally {
      setPulling(false);
    }
  }, []);

  // ── Process a single row, updating state ────────────────────────────────────
  const processRow = useCallback(async (idx: number): Promise<ProcessResult | null> => {
    setRows((rs) => rs.map((r, i) => i === idx ? { ...r, state: 'running' } : r));
    try {
      const result = await processOne(rows[idx].candidate);
      const newState: RowState['state'] =
        result.overallStatus === 'success' ? 'done' :
        result.overallStatus === 'skipped' ? 'skipped' :
        'failed';
      setRows((rs) => rs.map((r, i) => i === idx ? { ...r, state: newState, result } : r));
      return result;
    } catch (err: any) {
      setRows((rs) => rs.map((r, i) => i === idx ? {
        ...r, state: 'failed', result: {
          returnId:      r.candidate.ReturnID,
          clientCode:    r.candidate.ClientCode,
          overallStatus: 'error',
          error:         err.message,
          steps:         [],
        },
      } : r));
      return null;
    }
  }, [rows]);

  // ── Test on first row ───────────────────────────────────────────────────────
  const handleTest = useCallback(async () => {
    if (rows.length === 0) return;
    setRunning(true);
    try {
      setTestedIdx(0);
      setExpandedIdx(0);
      const result = await processRow(0);
      if (result?.overallStatus === 'success') {
        toast.info(`Test ok — verify in M-Files / CCH then execute the rest`);
      } else {
        toast.error(`Test failed: ${result?.error || 'unknown error'}`);
      }
    } finally {
      setRunning(false);
    }
  }, [rows, processRow]);

  // ── Retry only failed + skipped rows ────────────────────────────────────────
  // Resets target rows to 'pending' so the queue is visible, then runs the same
  // worker pool. Useful after fixing CCH-side blockers (Prohibit Changes,
  // missing taxpayer name, etc.) without re-pulling the full candidate list.
  const handleRetry = useCallback(async () => {
    setRunning(true);
    try {
      const indices: number[] = [];
      for (let i = 0; i < rows.length; i++) {
        if (rows[i].state === 'failed' || rows[i].state === 'skipped') indices.push(i);
      }
      if (indices.length === 0) {
        toast.info('Nothing to retry');
        return;
      }

      // Reset states up-front so the user sees the new queue immediately.
      const retrySet = new Set(indices);
      setRows((rs) => rs.map((r, i) =>
        retrySet.has(i) ? { ...r, state: 'pending', result: undefined } : r
      ));

      let cursor = 0;
      const worker = async () => {
        while (cursor < indices.length) {
          const idx = indices[cursor++];
          await processRow(idx);
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(EXECUTE_CONCURRENCY, indices.length) }, worker),
      );

      toast.info(`Retried ${indices.length} return${indices.length === 1 ? '' : 's'}`);
    } finally {
      setRunning(false);
    }
  }, [rows, processRow]);

  // ── Execute all (or all-except-tested) ──────────────────────────────────────
  const handleExecute = useCallback(async (excludeTested: boolean) => {
    setConfirmExec(false);
    setRunning(true);
    try {
      // Build the work list once — indices to process, in order.
      const indices: number[] = [];
      for (let i = 0; i < rows.length; i++) {
        if (excludeTested && testedIdx === i) continue;
        if (rows[i].state === 'done') continue;          // skip already-completed (e.g. tested)
        indices.push(i);
      }
      if (indices.length === 0) {
        toast.info('Nothing to process');
        return;
      }

      // Worker pool: each worker pulls the next index off a shared cursor.
      // No auto-expand here — multiple rows running at once would cause flicker.
      let cursor = 0;
      const worker = async () => {
        while (cursor < indices.length) {
          const idx = indices[cursor++];
          await processRow(idx);
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(EXECUTE_CONCURRENCY, indices.length) }, worker),
      );

      toast.info(`Processed ${indices.length} return${indices.length === 1 ? '' : 's'} — see per-row results`);
    } finally {
      setRunning(false);
    }
  }, [rows, testedIdx, processRow]);

  const total      = rows.length;
  const succeeded  = rows.filter((r) => r.state === 'done').length;
  const skipped    = rows.filter((r) => r.state === 'skipped').length;
  const failed     = rows.filter((r) => r.state === 'failed').length;
  const remaining  = rows.filter((r) => r.state === 'pending').length;

  // ── Summary rows: derived from any row that has a result ────────────────────
  const summaryRows = useMemo(() => buildSummaryRows(rows), [rows]);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Utility: MS 990 Batch Extension</h1>
        <p className="muted" style={{ margin: '4px 0 0' }}>
          Pulls candidate 990 returns from the <code>990extensions</code> Logic App. Per return: imports the type into the
          Form 8868 worksheet, calculates, prints, extracts the 8868 page, saves to M-Files, and (if not already accepted)
          uploads and releases the extension to the IRS.
        </p>
      </div>

      {/* Pre-flight notice */}
      <div style={{
        background: 'rgba(255,180,90,.06)', border: '1px solid rgba(255,180,90,.2)',
        borderRadius: 8, padding: 12, fontSize: 13, display: 'flex', gap: 10,
      }}>
        <AlertTriangle size={16} style={{ color: 'var(--warn)', flexShrink: 0, marginTop: 2 }} />
        <span>
          Close all 990 returns in CCH Tax before running. Every CCH state-change call rejects an in-use return.
          Run the test on the first record, verify in CCH/M-Files, then execute the rest.
        </span>
      </div>

      {/* Pull */}
      <div className="card">
        <div className="hd" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Database size={16} />
          <span style={{ fontWeight: 600, flex: 1 }}>Candidates</span>
          {total > 0 && (
            <span className="muted" style={{ fontSize: 12 }}>
              {total} to process · {succeeded} ok · {skipped} skipped · {failed} failed · {remaining} pending
              {hiddenCompletedCount > 0 && ` · ${hiddenCompletedCount} previously completed (hidden)`}
            </span>
          )}
        </div>
        <div className="bd">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="btn primary"
              disabled={pulling || running}
              onClick={handlePull}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {pulling
                ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Pulling...</>
                : <><Database size={14} /> Pull Candidates</>}
            </button>
            <span className="muted" style={{ fontSize: 12 }}>
              calls <code>/api/portal-data?type=990extensions</code>
            </span>
          </div>
        </div>
      </div>

      {/* Candidates table + per-row results */}
      {rows.length > 0 && (
        <div className="card">
          <div className="hd" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontWeight: 600, flex: 1 }}>Returns</span>
            <span className="muted" style={{ fontSize: 11 }}>scroll for more</span>
          </div>
          <div className="bd" style={{ overflow: 'auto', maxHeight: 360, padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={stickyThStyle}>#</th>
                  <th style={stickyThStyle}>Return ID</th>
                  <th style={stickyThStyle}>Client</th>
                  <th style={stickyThStyle}>Job Name</th>
                  <th style={stickyThStyle}>State</th>
                  <th style={stickyThStyle}>Steps</th>
                  <th style={stickyThStyle}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const isTested = testedIdx === i;
                  return (
                    <React.Fragment key={`${r.candidate.ReturnID}-${i}`}>
                      <tr style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
                        <td style={tdStyle}>{i + 1}</td>
                        <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.candidate.ReturnID}</td>
                        <td style={tdStyle}>
                          <div>{r.candidate.ClientName}</div>
                          <div className="muted" style={{ fontSize: 11 }}>{r.candidate.ClientCode}</div>
                        </td>
                        <td style={tdStyle}>{r.candidate.Job_Name}</td>
                        <td style={tdStyle}>
                          {r.state === 'pending' && (
                            <span className="muted">pending{isTested ? ' (test row)' : ''}</span>
                          )}
                          {r.state === 'running'  && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent)' }} />}
                          {r.state === 'done'     && (
                            <span style={{ color: 'var(--good)' }}>
                              <CheckCircle2 size={13} style={{ verticalAlign: -2 }} />{' '}
                              {r.result?.skippedEfile ? 'ok (e-file skipped)' : 'ok'}
                            </span>
                          )}
                          {r.state === 'skipped'  && (
                            <span style={{ color: 'var(--warn)' }} title={r.result?.error}>
                              <AlertTriangle size={13} style={{ verticalAlign: -2 }} /> skipped
                            </span>
                          )}
                          {r.state === 'failed'   && (
                            <span style={{ color: 'var(--bad)' }} title={r.result?.error}>
                              <XCircle size={13} style={{ verticalAlign: -2 }} /> failed
                            </span>
                          )}
                        </td>
                        <td style={tdStyle}><StepProgress steps={r.result?.steps} /></td>
                        <td style={tdStyle}>
                          {r.result && (
                            <button
                              className="btn"
                              style={{ padding: '2px 6px', fontSize: 11 }}
                              onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                            >
                              {expandedIdx === i ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            </button>
                          )}
                        </td>
                      </tr>
                      {expandedIdx === i && r.result && (
                        <tr>
                          <td colSpan={7} style={{ padding: 0 }}>
                            <div style={{
                              background: 'rgba(0,0,0,.18)', padding: 12, fontSize: 12,
                              borderTop: '1px solid var(--border)',
                            }}>
                              {r.result.error && (
                                <div style={{
                                  background: 'rgba(255,107,107,.08)', border: '1px solid rgba(255,107,107,.2)',
                                  borderRadius: 6, padding: 8, marginBottom: 10, color: 'var(--bad)',
                                }}>
                                  <strong>Error:</strong> {r.result.error}
                                </div>
                              )}
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                <thead>
                                  <tr>
                                    <th style={thStyle}>Step</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={thStyle}>Detail</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.result.steps.map((s, si) => {
                                    const { name, status, message, ...extras } = s;
                                    const detail = message || (Object.keys(extras).length
                                      ? JSON.stringify(extras)
                                      : '');
                                    return (
                                      <tr key={si}>
                                        <td style={tdStyle}>{name}</td>
                                        <td style={tdStyle}>
                                          <StepDot status={status} /> {status}
                                        </td>
                                        <td style={{ ...tdStyle, fontFamily: 'monospace', whiteSpace: 'normal' }}>
                                          {detail}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Test on first */}
      {rows.length > 0 && (
        <div className="card" style={{ border: testedIdx === null ? '1px solid var(--accent)' : undefined }}>
          <div className="hd" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FlaskConical size={16} style={{ color: 'var(--accent)' }} />
            <span style={{ fontWeight: 600, flex: 1 }}>Test on First Row</span>
          </div>
          <div className="bd" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="btn primary"
              disabled={running || rows.length === 0}
              onClick={handleTest}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {running && testedIdx === 0 && rows[0]?.state === 'running'
                ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Running...</>
                : <><Play size={14} /> Test Row 1</>}
            </button>
            <span className="muted" style={{ fontSize: 12 }}>
              Runs the full pipeline against row 1 only — typically ~3 min.
            </span>
          </div>
        </div>
      )}

      {/* Execute all */}
      {rows.length > 0 && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Execute</div>
          <div className="bd" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              className="btn primary"
              disabled={running || rows.length === 0}
              onClick={() => setConfirmExec(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {running
                ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Processing...</>
                : <><Play size={14} /> Execute All</>}
            </button>
            <button
              className="btn"
              disabled={running || (failed + skipped) === 0}
              onClick={handleRetry}
              title="Re-run only the rows that failed or were skipped — preserves the OK rows. Use this after fixing CCH-side blockers."
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <RotateCcw size={14} /> Retry Failed / Skipped ({failed + skipped})
            </button>
            <span className="muted" style={{ fontSize: 12 }}>
              Concurrent ({EXECUTE_CONCURRENCY} at a time) — ~3 min per return. Don't open any of them in CCH while this is running.
            </span>
          </div>
        </div>
      )}

      {/* Summary — appears once at least one row has a result */}
      {summaryRows.length > 0 && (
        <div className="card">
          <div className="hd" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CheckCircle2 size={16} />
            <span style={{ fontWeight: 600, flex: 1 }}>
              Summary <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>
                ({summaryRows.length} processed · {summaryRows.filter(s => s.status === 'OK').length} ok ·{' '}
                {summaryRows.filter(s => s.status === 'SKIPPED').length} skipped ·{' '}
                {summaryRows.filter(s => s.status === 'FAILED').length} failed ·{' '}
                {summaryRows.filter(s => s.alreadyAccepted === 'YES').length} already accepted)
              </span>
            </span>
            <button
              className="btn"
              onClick={() => downloadCsv(summaryRows)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
            >
              <Download size={13} /> CSV
            </button>
            <button
              className="btn"
              onClick={() => downloadXlsx(summaryRows)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
            >
              <FileSpreadsheet size={13} /> Excel
            </button>
          </div>
          <div className="bd" style={{ overflow: 'auto', maxHeight: 480, padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={stickyThStyle}>#</th>
                  <th style={stickyThStyle}>Return ID</th>
                  <th style={stickyThStyle}>Client</th>
                  <th style={stickyThStyle}>Status</th>
                  <th style={stickyThStyle}>Already Accepted</th>
                  <th style={stickyThStyle}>E-File Submitted</th>
                  <th style={stickyThStyle}>Upload Key</th>
                  <th style={stickyThStyle}>M-Files</th>
                  <th style={stickyThStyle}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {summaryRows.map((s, i) => (
                  <tr key={`sum-${s.returnId}-${i}`} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
                    <td style={tdStyle}>{i + 1}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{s.returnId}</td>
                    <td style={tdStyle}>
                      <div>{s.clientName}</div>
                      <div className="muted" style={{ fontSize: 11 }}>{s.clientCode}</div>
                    </td>
                    <td style={tdStyle}>
                      {s.status === 'OK'      && <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> OK</span>}
                      {s.status === 'SKIPPED' && <span style={{ color: 'var(--warn)' }}><AlertTriangle size={12} style={{ verticalAlign: -2 }} /> SKIPPED</span>}
                      {s.status === 'FAILED'  && <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> FAILED</span>}
                    </td>
                    <td style={tdStyle}>{s.alreadyAccepted}</td>
                    <td style={tdStyle}>
                      {s.efileSubmitted === 'YES'     && <span style={{ color: 'var(--good)' }}>YES</span>}
                      {s.efileSubmitted === 'SKIPPED' && <span className="muted">SKIPPED</span>}
                      {s.efileSubmitted === 'NO'      && <span className="muted">—</span>}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{s.uploadHistoryKey || '—'}</td>
                    <td style={tdStyle}>
                      {s.mfilesLink
                        ? <a href={s.mfilesLink} style={{ color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <ExternalLink size={11} /> Open
                          </a>
                        : '—'}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'normal', maxWidth: 320 }}>
                      <span style={{
                        color: s.status === 'FAILED'  ? 'var(--bad)' :
                               s.status === 'SKIPPED' ? 'var(--warn)' : 'var(--muted)',
                        fontSize: 11,
                      }}>
                        {s.notes || '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {confirmExec && (
        <ExecConfirmModal
          totalRows={rows.length}
          testedRowNum={testedIdx !== null ? testedIdx + 1 : null}
          onConfirm={(excludeTested) => handleExecute(excludeTested)}
          onCancel={() => setConfirmExec(false)}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

// ── Execute confirmation modal (pared-down version of ImportPage's) ──────────

interface ExecConfirmProps {
  totalRows: number;
  testedRowNum: number | null;
  onConfirm: (excludeTested: boolean) => void;
  onCancel: () => void;
}

function ExecConfirmModal({ totalRows, testedRowNum, onConfirm, onCancel }: ExecConfirmProps) {
  const [exclude, setExclude] = useState(true);
  const processCount = testedRowNum !== null && exclude ? totalRows - 1 : totalRows;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div style={{
        background: 'var(--surface, #1e2030)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 24, width: 460, maxWidth: '95vw',
        boxShadow: '0 8px 40px rgba(0,0,0,.6)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <AlertTriangle size={18} style={{ color: 'var(--warn)' }} />
          <span style={{ fontWeight: 700, fontSize: 16 }}>Execute MS 990 Batch Extension</span>
        </div>

        {testedRowNum !== null && (
          <div style={{
            background: 'rgba(122,162,255,.06)', border: '1px solid rgba(122,162,255,.15)',
            borderRadius: 8, padding: 12, marginBottom: 16,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              Row {testedRowNum} was already processed in the test
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {[
                { val: true,  label: `Skip row ${testedRowNum}`,    desc: 'Already complete' },
                { val: false, label: `Re-run row ${testedRowNum}`,  desc: 'Process it again' },
              ].map((opt) => (
                <label
                  key={String(opt.val)}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                    background: exclude === opt.val ? 'rgba(122,162,255,.1)' : 'transparent',
                    border: `1px solid ${exclude === opt.val ? 'rgba(122,162,255,.3)' : 'transparent'}`,
                  }}
                >
                  <input
                    type="radio" name="excludeTested"
                    checked={exclude === opt.val}
                    onChange={() => setExclude(opt.val)}
                    style={{ marginTop: 2, accentColor: 'var(--accent)' }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{opt.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>
          {processCount === 0
            ? 'Nothing to process — the tested row was the only one.'
            : <>This will process <strong style={{ color: 'var(--text)' }}>{processCount} return{processCount !== 1 ? 's' : ''}</strong> with {EXECUTE_CONCURRENCY} in flight concurrently. Estimated ~{Math.round((processCount * 3) / EXECUTE_CONCURRENCY)} min.</>}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onCancel} style={{ fontSize: 13 }}>Cancel</button>
          <button
            className="btn"
            onClick={() => onConfirm(exclude)}
            disabled={processCount === 0}
            style={{
              fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(122,162,255,.15)', borderColor: 'rgba(122,162,255,.5)',
              color: 'var(--accent)', opacity: processCount === 0 ? .45 : 1,
            }}
          >
            <Play size={13} /> Run {processCount}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Summary row construction + downloads ────────────────────────────────────

interface SummaryRow {
  returnId: string;
  clientName: string;
  clientCode: string;
  jobName: string;
  status: 'OK' | 'SKIPPED' | 'FAILED';
  alreadyAccepted: 'YES' | 'NO' | 'N/A';
  efileSubmitted: 'YES' | 'SKIPPED' | 'NO';
  uploadHistoryKey: string;
  mfilesLink: string;
  notes: string;
}

function findStep(steps: ProcessStep[] | undefined, name: string): ProcessStep | undefined {
  return (steps || []).find((s) => s.name === name);
}

function buildSummaryRows(rows: RowState[]): SummaryRow[] {
  return rows
    .filter((r) => r.result)
    .map((r) => {
      const result    = r.result!;
      const mf        = findStep(result.steps, 'saveMFiles');
      const release   = findStep(result.steps, 'release');
      const accepted  = findStep(result.steps, 'checkAccepted');
      const status: SummaryRow['status'] =
        result.overallStatus === 'success' ? 'OK'      :
        result.overallStatus === 'skipped' ? 'SKIPPED' : 'FAILED';
      const alreadyAccepted: SummaryRow['alreadyAccepted'] =
        accepted?.status === 'ok'
          ? (accepted as { alreadyAccepted?: boolean }).alreadyAccepted ? 'YES' : 'NO'
          : 'N/A';
      const efileSubmitted: SummaryRow['efileSubmitted'] =
        result.skippedEfile     ? 'SKIPPED' :
        release?.status === 'ok' ? 'YES'    : 'NO';

      return {
        returnId:         r.candidate.ReturnID,
        clientName:       r.candidate.ClientName,
        clientCode:       r.candidate.ClientCode,
        jobName:          r.candidate.Job_Name,
        status,
        alreadyAccepted,
        efileSubmitted,
        uploadHistoryKey: String((release as { uploadHistoryKey?: string | number })?.uploadHistoryKey ?? ''),
        mfilesLink:       String((mf as { mfilesLink?: string })?.mfilesLink || ''),
        notes:            result.error || (status === 'OK' ? '' : 'Failed without explicit error'),
      };
    });
}

const SUMMARY_HEADERS = [
  'Return ID', 'Client', 'Client Code', 'Job Name',
  'Status', 'Already Accepted', 'E-File Submitted',
  'Upload History Key', 'M-Files Link', 'Notes',
] as const;

function summaryRowToArray(s: SummaryRow): (string | number)[] {
  return [
    s.returnId, s.clientName, s.clientCode, s.jobName,
    s.status, s.alreadyAccepted, s.efileSubmitted,
    s.uploadHistoryKey, s.mfilesLink, s.notes,
  ];
}

function csvCell(v: string | number): string {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(rows: SummaryRow[]) {
  const lines = [SUMMARY_HEADERS.join(',')];
  for (const r of rows) lines.push(summaryRowToArray(r).map(csvCell).join(','));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  triggerDownload(blob, `ms990-batch-extension-${stamp()}.csv`);
}

function downloadXlsx(rows: SummaryRow[]) {
  const aoa: (string | number)[][] = [SUMMARY_HEADERS.slice() as unknown as string[]];
  for (const r of rows) aoa.push(summaryRowToArray(r));

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Replace the M-Files Link column (index 8) with an Excel HYPERLINK formula
  // so the cell renders as "Click to Open in M-Files" and opens the desktop app.
  const linkCol = 8;
  for (let r = 1; r < aoa.length; r++) {
    const url = String(aoa[r][linkCol] ?? '');
    if (!url) continue;
    const cellAddr = XLSX.utils.encode_cell({ r, c: linkCol });
    const safeUrl  = url.replace(/"/g, '""');
    ws[cellAddr] = {
      t: 's',
      v: 'Click to Open in M-Files',
      f: `HYPERLINK("${safeUrl}","Click to Open in M-Files")`,
    };
  }

  // Reasonable column widths
  ws['!cols'] = [
    { wch: 22 }, { wch: 32 }, { wch: 12 }, { wch: 36 },
    { wch: 8  }, { wch: 16 }, { wch: 16 },
    { wch: 16 }, { wch: 28 }, { wch: 40 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Results');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  triggerDownload(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `ms990-batch-extension-${stamp()}.xlsx`,
  );
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function stamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}

// ── Shared styles ────────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid var(--border)',
  fontSize: 11, fontWeight: 600, color: 'var(--muted)', whiteSpace: 'nowrap',
};

// Sticky version for the outer scrollable candidates table — keeps the header
// visible while scrolling through the rows. Background color matches the card
// surface so rows scrolling under it don't bleed through.
const stickyThStyle: React.CSSProperties = {
  ...thStyle,
  position: 'sticky', top: 0, zIndex: 1,
  background: 'var(--surface, #1e2030)',
};

const tdStyle: React.CSSProperties = {
  padding: '5px 10px', borderBottom: '1px solid rgba(255,255,255,.04)', whiteSpace: 'nowrap',
};
